import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '../Button'
import { useScrollLock } from '../../hooks'
import styles from './WaitlistModal.module.css'

interface WaitlistModalProps {
  isOpen: boolean
  onClose: () => void
}

export function WaitlistModal({ isOpen, onClose }: WaitlistModalProps) {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Base fields
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [comments, setComments] = useState('')

  // Lean Startup Expansion
  const [showLeanQuestions, setShowLeanQuestions] = useState(false)
  
  // Tier 1
  const [lastPurchaseTime, setLastPurchaseTime] = useState('')
  const [lastPurchaseImportant, setLastPurchaseImportant] = useState('')
  const [boughtRecycledBefore, setBoughtRecycledBefore] = useState('')
  const [lastPurchaseFrustration, setLastPurchaseFrustration] = useState('')
  
  // Tier 2
  const [otherRecycledProducts, setOtherRecycledProducts] = useState('')
  const [customizationImportance, setCustomizationImportance] = useState('')
  const [prescriptionProcess, setPrescriptionProcess] = useState('')
  
  const modalRef = useRef<HTMLDivElement>(null)

  useScrollLock(isOpen)

  useEffect(() => {
    if (isOpen) {
      modalRef.current?.focus()
      // Reset state when opened
      setIsSubmitted(false)
      setName('')
      setEmail('')
      setComments('')
      setShowLeanQuestions(false)
      setLastPurchaseTime('')
      setLastPurchaseImportant('')
      setBoughtRecycledBefore('')
      setLastPurchaseFrustration('')
      setOtherRecycledProducts('')
      setCustomizationImportance('')
      setPrescriptionProcess('')
    }
  }, [isOpen])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      const formData = new FormData()
      formData.append('form-name', 'waitlist')
      formData.append('name', name)
      formData.append('email', email)
      formData.append('comments', comments)
      
      if (showLeanQuestions) {
        formData.append('lastPurchaseTime', lastPurchaseTime)
        formData.append('lastPurchaseImportant', lastPurchaseImportant)
        formData.append('boughtRecycledBefore', boughtRecycledBefore)
        formData.append('lastPurchaseFrustration', lastPurchaseFrustration)
        formData.append('otherRecycledProducts', otherRecycledProducts)
        formData.append('customizationImportance', customizationImportance)
        formData.append('prescriptionProcess', prescriptionProcess)
      }

      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString()
      })
      
      setIsSubmitted(true)
    } catch (error) {
      console.error('Error submitting form:', error)
      setIsSubmitted(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />
      <div
        ref={modalRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="waitlist-title"
        tabIndex={-1}
      >
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Cerrar modal"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {!isSubmitted ? (
          <div className={styles.scrollableContent}>
            <div className={styles.content}>
              <h2 id="waitlist-title" className={styles.title}>¡Agradecemos mucho tu interés!</h2>
              <p className={styles.description}>
                Estamos trabajando duro para lanzar nuestro producto a finales del próximo mes.
              </p>
              
              {!showLeanQuestions && (
                <div className={styles.discountBadge}>
                  Regístrate en la lista de espera y obtén un <strong>10% de descuento</strong> en tu primera compra.
                </div>
              )}

              {showLeanQuestions && (
                <div className={styles.discountBadgeSuper}>
                  ¡Genial! Responde estas breves preguntas y tu descuento subirá al <strong>15% en tu primera compra</strong>.
                </div>
              )}

              <form name="waitlist" data-netlify="true" onSubmit={handleSubmit} className={styles.form}>
                <input type="hidden" name="form-name" value="waitlist" />
                
                <div className={styles.formGroup}>
                  <label htmlFor="name" className={styles.label}>Nombre *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className={styles.input}
                    placeholder="Tu nombre"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="email" className={styles.label}>Correo Electrónico *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={styles.input}
                    placeholder="tucorreo@ejemplo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="comments" className={styles.label}>¿Qué te gustaría preguntar, saber o añadir? (Opcional)</label>
                  <textarea
                    id="comments"
                    name="comments"
                    className={styles.textarea}
                    placeholder="Tus comentarios nos ayudan a mejorar..."
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    rows={2}
                  />
                </div>

                {!showLeanQuestions ? (
                  <div className={styles.leanTeaser}>
                    <button 
                      type="button" 
                      className={styles.leanToggleButton} 
                      onClick={() => setShowLeanQuestions(true)}
                    >
                      <span className={styles.unlockIcon}>🔓</span> Ayúdanos a construir nuestra marca y <strong>desbloquea un 5% extra</strong>
                    </button>
                  </div>
                ) : (
                  <div className={styles.leanSection}>
                    <h3 className={styles.leanTitle}>Ayúdanos a mejorar (+5% extra)</h3>
                    
                    <div className={styles.formGroup}>
                      <label htmlFor="lastPurchaseTime" className={styles.label}>¿Cuándo fue la última vez que compraste lentes o armazones?</label>
                      <input
                        type="text"
                        id="lastPurchaseTime"
                        className={styles.input}
                        value={lastPurchaseTime}
                        onChange={(e) => setLastPurchaseTime(e.target.value)}
                        required={showLeanQuestions}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="lastPurchaseImportant" className={styles.label}>¿Qué fue lo más importante en esa compra?</label>
                      <input
                        type="text"
                        id="lastPurchaseImportant"
                        className={styles.input}
                        value={lastPurchaseImportant}
                        onChange={(e) => setLastPurchaseImportant(e.target.value)}
                        required={showLeanQuestions}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="lastPurchaseFrustration" className={styles.label}>¿Qué te frustró de tu última compra de lentes?</label>
                      <input
                        type="text"
                        id="lastPurchaseFrustration"
                        className={styles.input}
                        value={lastPurchaseFrustration}
                        onChange={(e) => setLastPurchaseFrustration(e.target.value)}
                        required={showLeanQuestions}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="boughtRecycledBefore" className={styles.label}>¿Has comprado antes productos hechos con materiales reciclados?</label>
                      <select 
                        id="boughtRecycledBefore"
                        className={styles.input}
                        value={boughtRecycledBefore}
                        onChange={(e) => setBoughtRecycledBefore(e.target.value)}
                        required={showLeanQuestions}
                      >
                        <option value="">Selecciona una opción</option>
                        <option value="Si">Sí</option>
                        <option value="No">No</option>
                        <option value="No estoy seguro">No estoy seguro/a</option>
                      </select>
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="otherRecycledProducts" className={styles.label}>Además de lentes, ¿te interesarían otros productos hechos de plástico reciclado? ¿Cuáles?</label>
                      <input
                        type="text"
                        id="otherRecycledProducts"
                        className={styles.input}
                        value={otherRecycledProducts}
                        onChange={(e) => setOtherRecycledProducts(e.target.value)}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="customizationImportance" className={styles.label}>¿Qué tan importante sería para ti poder personalizar tu propia carátula?</label>
                      <select 
                        id="customizationImportance"
                        className={styles.input}
                        value={customizationImportance}
                        onChange={(e) => setCustomizationImportance(e.target.value)}
                      >
                        <option value="">Selecciona una opción</option>
                        <option value="Mucho">Mucho</option>
                        <option value="Algo">Algo</option>
                        <option value="Poco">Poco</option>
                        <option value="Nada">Nada</option>
                      </select>
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="prescriptionProcess" className={styles.label}>Si compraras con nosotros, ¿cómo preferirías el proceso de graduación?</label>
                      <select 
                        id="prescriptionProcess"
                        className={styles.input}
                        value={prescriptionProcess}
                        onChange={(e) => setPrescriptionProcess(e.target.value)}
                      >
                        <option value="">Selecciona una opción</option>
                        <option value="Que ustedes gestionen la graduación">Que ustedes gestionen la graduación</option>
                        <option value="Mandar mi receta">Mandar mi receta</option>
                        <option value="Comprar solo armazón / carátula">Comprar solo armazón / carátula</option>
                        <option value="Aún no lo sé">Aún no lo sé</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className={styles.actions}>
                  <Button fullWidth size="lg" disabled={isSubmitting}>
                    {isSubmitting ? 'Enviando...' : 'Unirme a la lista de espera'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div className={styles.successContent}>
            <div className={styles.successIcon}>✓</div>
            <h2 className={styles.title}>¡Gracias por registrarte!</h2>
            {showLeanQuestions ? (
              <p className={styles.description}>
                Valoramos muchísimo tus respuestas. Te hemos añadido a la lista y tienes un <strong>15% de descuento garantizado</strong> para tu primera compra. Pronto recibirás noticias nuestras.
              </p>
            ) : (
              <p className={styles.description}>
                Te hemos añadido a la lista. Pronto recibirás noticias nuestras y tu descuento del <strong>10%</strong>.
              </p>
            )}
            <div className={styles.actions}>
              <Button fullWidth size="lg" onClick={onClose}>
                Continuar navegando
              </Button>
            </div>
          </div>
        )}
      </div>
    </>,
    document.body
  )
}
